import React, { useState, useMemo, useCallback } from "react";
import * as XLSX from "xlsx";
import { Table, Button } from 'react-bootstrap';

// RTK Query hooks
import { useGetStoreSalesQuery } from "../../slices/storeSalesApiSlice";
import { useGetStoreExpensesQuery } from "../../slices/storeExpensesApiSlice";
import { useGetStoreIncomesQuery } from "../../slices/storeIncomesApiSlice";
import { useGetStoreDepositsQuery } from "../../slices/storeDepositsApiSlice";
import { useGetStoreSuppliesQuery } from "../../slices/storeSuppliesApiSlice";
import { useGetStoreStocksQuery } from "../../slices/storeStocksApiSlice";
import { useGetStoreTransfersQuery } from "../../slices/storeTransfersApiSlice";

export default function StoreStockDateStoreExpenses() {

  const safeFixed = (value) => Number(value || 0).toFixed(2);
  const today = new Date().toISOString().split("T")[0];
  const [date1, setDate1] = useState(today);
  const [date2, setDate2] = useState(today);
  const [reportData, setReportData] = useState([]);
  const [totals, setTotals] = useState({});

  // Fetch data
  const { data: storeSalesData, isLoading: storeSalesLoading } = useGetStoreSalesQuery();
  const { data: storeExpensesData, isLoading: storeExpensesLoading } = useGetStoreExpensesQuery({ startDate: today, endDate: today });
  const { data: storeIncomesData, isLoading: storeIncomesLoading } = useGetStoreIncomesQuery({ startDate: today, endDate: today });
  const { data: storeDepositsData, isLoading: storeDepositsLoading } = useGetStoreDepositsQuery({ startDate: today, endDate: today });
  const { data: storeSuppliesData, isLoading: storeSuppliesLoading } = useGetStoreSuppliesQuery();
  const { data: storeTransfersData } = useGetStoreTransfersQuery();
  const { data: storeStocksData } = useGetStoreStocksQuery();

  const formatDate = date => new Date(date).toLocaleDateString("en-CA");
  const getDatesBetween = (start, end) => {
    const dates = [];
    let curr = new Date(start);
    const endDate = new Date(end);
    while (curr < endDate) {
      dates.push(formatDate(curr));
      curr.setDate(curr.getDate() + 1);
    }
    return dates;
  };

  const storeSales = useMemo(() => storeSalesData?.storeSales || [], [storeSalesData]);
  const storeExpenses = useMemo(() => storeExpensesData?.storeExpenses || [], [storeExpensesData]);
  const storeIncomes = useMemo(() => storeIncomesData?.storeIncomes || [], [storeIncomesData]);
  const storeDeposits = useMemo(() => storeDepositsData?.storeDeposits || [], [storeDepositsData]);
  const storeSupplies = useMemo(() => storeSuppliesData?.storeSupplies || [], [storeSuppliesData]);
  const storeTransfers = useMemo(() => storeTransfersData?.storeTransfers || [], [storeTransfersData]);
  const storeStocks = useMemo(() => storeStocksData?.storeStocks || [], [storeStocksData]);

  const calculateReport = () => {
    const dates = getDatesBetween(date1, date2);
    const result = dates.map(date => {
      const netStoreSales = storeSales
        .filter(s => formatDate(s.date) === date)
        .reduce((sum, s) => sum + (
          s.storeSaleItems?.reduce((sItemSum, item) =>
            sItemSum + (Number(item.price) || 0) * (Number(item.qty) || 0), 0) || 0
        ), 0);

      const netStoreExpenses = storeExpenses
        .filter(e => formatDate(e.date) === date)
        .reduce((sum, e) => sum + Number(e.amount || 0), 0);

      const cashCollected = storeIncomes
        .filter(i => formatDate(i.date) === date)
        .reduce((sum, i) => sum + Number(i.amount || 0), 0);

      const storeDepositTotal = storeDeposits
        .filter(d => formatDate(d.date) === date)
        .reduce((sum, d) => sum + Number(d.amount || 0), 0);

      const netBank = storeDepositTotal * -1;

      const purchases = storeSupplies
        .filter(sup => formatDate(sup.date) === date)
        .reduce((sum, sup) => sum + Number(sup.cardCost || 0), 0) * -1;

      const netCash = netStoreSales + netStoreExpenses;
      const difference = cashCollected - netCash;
      const cashInHand = netCash + netBank + purchases;

      return { date, netStoreSales, netStoreExpenses, netCash, cashCollected, difference, netBank, purchases, cashInHand };
    });

    setReportData(result);
    const totals = result.reduce((acc, r) => ({
      totalStoreSales: acc.totalStoreSales + r.netStoreSales,
      totalStoreExpenses: acc.totalStoreExpenses + r.netStoreExpenses,
      totalNetCash: acc.totalNetCash + r.netCash,
      totalStoreIncomes: acc.totalStoreIncomes + r.cashCollected,
      totalDifference: acc.totalDifference + r.difference,
      totalStoreDeposit: acc.totalStoreDeposit + r.netBank,
      totalPurchases: acc.totalPurchases + r.purchases,
      totalCashInHand: acc.totalCashInHand + r.cashInHand,
    }), {
      totalStoreSales: 0, totalStoreExpenses: 0, totalNetCash: 0,
      totalStoreIncomes: 0, totalDifference: 0,
      totalStoreDeposit: 0, totalPurchases: 0,
      totalCashInHand: 0
    });

    setTotals(totals);
  };

  const calculateStoreStockValue = useCallback((targetDate) => {
    return storeStocks
      .filter(s => formatDate(s.date) === formatDate(targetDate))
      .flatMap(s => s.storeStockItems || [])
      .reduce((sum, item) => sum + Number(item.price || 0) * Number(item.qty || 0), 0);
  }, [storeStocks]);

  const calculateStoreSupplyValue = useCallback((start, end) => {
    return storeSupplies
      .filter(s => formatDate(s.date) >= formatDate(start) && formatDate(s.date) < formatDate(end))
      .flatMap(s => s.storeSupplyItems || [])
      .reduce((sum, item) => sum + Number(item.price || 0) * Number(item.qty || 0), 0);
  }, [storeSupplies]);

  const calculateStoreTransferValue = useCallback((start, end) => {
    return storeTransfers
      .filter(t => new Date(t.date) >= new Date(start) && new Date(t.date) < new Date(end))
      .flatMap(t => t.storeTransferItems || [])
      .reduce((sum, item) => sum + Number(item.price || 0) * Number(item.qty || 0), 0);
  }, [storeTransfers]);

  const calculateStoreExpenseValue = useCallback((start, end) => {
    return storeExpenses
      .filter(e => new Date(e.date) >= new Date(start) && new Date(e.date) < new Date(end))
      .reduce((sum, e) => sum + Number(e.amount || 0), 0);
  }, [storeExpenses]);


  const calculateStoreIncomeValue = useCallback((start, end) => {
    return storeIncomes
      .filter(i => new Date(i.date) >= new Date(start) && new Date(i.date) < new Date(end))
      .reduce((sum, i) => sum + Number(i.amount || 0), 0);
  }, [storeIncomes]);

  // Function to calculate only StoreSalesCredit-related storeExpenses within the date range
    const calculateStoreSalesCreditStoreExpenseValue = (start, end) => {
      return storeExpensesData?.storeExpenses
        ?.filter(storeExpense => {
          const storeExpenseDate = new Date(storeExpense.date).toISOString().split('T')[0];
          return storeExpense.category === "StoreSalesCredit" && storeExpenseDate >= start && storeExpenseDate < end;
        })
        .reduce((sum, storeExpense) => sum + Number(storeExpense.amount || 0), 0) || 0;
    };
  
    const calculateStoreSalesPaymentStoreExpenseValue = (start, end) => {
    return storeExpensesData?.storeExpenses
      ?.filter(storeExpense => {
        const storeExpenseDate = new Date(storeExpense.date).toISOString().split('T')[0];
        return storeExpense.category === "StoreSalesPayment" && storeExpenseDate >= start && storeExpenseDate < end;
      })
      .reduce((sum, storeExpense) => sum + Number(storeExpense.amount || 0), 0) || 0;
  };
  

  const openingStoreStock = calculateStoreStockValue(date1);
  const closingStoreStock = calculateStoreStockValue(date2);
  const storeSupplyVal = calculateStoreSupplyValue(date1, date2);
  const storeTransferVal = calculateStoreTransferValue(date1, date2);
  const storeExpenseVal = calculateStoreExpenseValue(date1, date2);
  const storeIncomeVal = calculateStoreIncomeValue(date1, date2);
  const netValue = openingStoreStock + storeSupplyVal + storeExpenseVal - storeTransferVal - closingStoreStock;
  const variation = storeIncomeVal - netValue;

  const storeExpenseStoreSalesCreditTotal = calculateStoreSalesCreditStoreExpenseValue(date1, date2);
  
  const storeExpenseStoreSalesPaymentTotal = calculateStoreSalesPaymentStoreExpenseValue(date1, date2);
       const increaseInReceivables = storeExpenseStoreSalesCreditTotal + storeExpenseStoreSalesPaymentTotal;
       const reValue = increaseInReceivables < 0 
    ? variation - increaseInReceivables 
    : variation;
  

  const exportToExcel = () => {
    const exportData = reportData.map(row => ({
      Date: row.date,
      NetStoreSales: row.netStoreSales.toFixed(2),
      NetStoreExpenses: row.netStoreExpenses.toFixed(2),
      NetCash: row.netCash.toFixed(2),
      CashCollected: row.cashCollected.toFixed(2),
      Difference: row.difference.toFixed(2),
      NetBank: row.netBank.toFixed(2),
      Purchases: row.purchases.toFixed(2),
      CashInHand: row.cashInHand.toFixed(2),
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Balance Report");
    XLSX.writeFile(workbook, "BalanceReport.xlsx");
  };
    
  
  
  return (
    <>
      <div className="balance-report">
        <div className="date-range">
          <label>Start Date:
            <input type="date" value={date1} onChange={e => setDate1(e.target.value)} />
          </label>
          <label>End Date:
            <input type="date" value={date2} onChange={e => setDate2(e.target.value)} />
          </label>
        </div>
        <button onClick={calculateReport} className="calculate-button">
          Calculate Report
        </button>

        {storeSalesLoading || storeExpensesLoading || storeIncomesLoading || storeDepositsLoading || storeSuppliesLoading ? (
          <div>Loading data...</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Net StoreSales</th>
                <th>Net StoreExpenses</th>
                <th>Net Cash</th>
                <th>Cash Collected</th>
                <th>Difference</th>
                <th>Net Bank</th>
                <th>Purchases</th>
                <th>Cash In Hand</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map(row => (
                <tr key={row.date}>
                  <td>{row.date}</td>
                  <td>{row.netStoreSales.toFixed(2)}</td>
                  <td>{row.netStoreExpenses.toFixed(2)}</td>
                  <td>{row.netCash.toFixed(2)}</td>
                  <td>{row.cashCollected.toFixed(2)}</td>
                  <td>{row.difference.toFixed(2)}</td>
                  <td>{row.netBank.toFixed(2)}</td>
                  <td>{row.purchases.toFixed(2)}</td>
                  <td>{row.cashInHand.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Total</strong></td>
                
                <td><strong>{safeFixed(totals.totalStoreSales)}</strong></td>
                <td><strong>{safeFixed(totals.totalStoreExpenses)}</strong></td>
                <td><strong>{safeFixed(totals.totalNetCash)}</strong></td>
                <td><strong>{safeFixed(totals.totalStoreIncomes)}</strong></td>
                <td><strong>{safeFixed(totals.totalDifference)}</strong></td>
                <td><strong>{safeFixed(totals.totalStoreDeposit)}</strong></td>
                <td><strong>{safeFixed(totals.totalPurchases)}</strong></td>
                <td><strong>{safeFixed(totals.totalCashInHand)}</strong></td>

              </tr>
            </tfoot>
          </table>
        )}

        <button onClick={exportToExcel} className="export-button">
          Export to Excel
        </button>
      </div>

      <h2>Working Capital Summary</h2>

      <Button onClick={calculateReport} variant="primary" className="mb-3">
        Calculate StoreStock Values
      </Button>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Date1</th>
            <th>Date2</th>
            <th>Opening StoreStock</th>
            <th>StoreSupply Value</th>
            <th>StoreTransfer Value</th>
            <th>StoreExpense Value</th>
            <th>Closing StoreStock</th>
            <th>Net Value</th>
            <th>StoreIncome Value</th>
            <th>Variation</th>
            <th style={{ display: 'none' }}>ReVar</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{date1}</td>
            <td>{date2}</td>
            <td>{openingStoreStock.toFixed(2)}</td>
            <td>{storeSupplyVal.toFixed(2)}</td>
            <td>{storeTransferVal.toFixed(2)}</td>
            <td>{storeExpenseVal.toFixed(2)}</td>
            <td>{closingStoreStock.toFixed(2)}</td>
            <td>{netValue.toFixed(2)}</td>
            <td>{storeIncomeVal.toFixed(2)}</td>
            <td>{variation.toFixed(2)}</td>
            <td style={{ display: 'none' }}>{reValue.toFixed(2)}</td>
          </tr>
        </tbody>
      </Table>
    </>
  );
}